/**
 * This file was auto-generated.
 * Do not make direct changes to the file.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
	QueryClient,
	useMutation,
	useInfiniteQuery,
	useQuery,
	useQueryClient,
	hashKey,
	MutationObserver,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions
} from '@tanstack/react-query'
import { ReactNode, useCallback, useMemo, useRef, createContext, useContext } from 'react'
import { BehaviorSubject, Observable, Subscription, filter, map } from 'rxjs'

import { Api } from './api'
import type {
	EndpointInput,
	EndpointPath,
	EndpointQuery,
	EndpointResponse,
	EndpointErrorResponses,
	EndpointNoThrowResult
} from './common'
import { Endpoint } from './common'
export const DEFAULT_PAGE_SIZE = 30

function getRandomId() {
	return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export const createStableQueryKey = (params: any) => {
	return Object.entries(params ?? {})
		.filter(([_, value]) => value !== undefined && value !== null)
		.map(([key, value]) => `${key}:${value}`)
		.join('&')
}

export function waitForObservable<T>(subject: Observable<T>) {
	return new Promise<T>((resolve, reject) => {
		let subscription: Subscription | undefined = undefined
		subscription = subject.subscribe(
			value => {
				resolve(value)
				subscription?.unsubscribe()
			},
			error => reject(error)
		)
	})
}

export function waitUntilTrue(subject: Observable<boolean>) {
	return waitForObservable(subject.pipe(filter(value => value)))
}

type QueueRequest<T> = { id: string; makeRequest: () => Promise<T> }
type QueueResult<T> = { id: string } & ({ successful: true; result: T } | { successful: false; error?: any })
export class OrderedSequentialQueue<T> {
	private requestQueue: QueueRequest<T>[] = []
	private requestComplete = new BehaviorSubject<QueueResult<T> | undefined>(undefined)
	private currentRequest = new BehaviorSubject<QueueRequest<T> | undefined>(undefined)

	constructor() {
		this.currentRequest.subscribe(async request => {
			if (!request) {
				return
			}
			try {
				const result = await request.makeRequest()
				this.requestComplete.next({ successful: true, id: request.id, result })
			} catch (e: any) {
				this.requestComplete.next({ successful: false, id: request.id, error: e })
			}
			this.currentRequest.next(this.requestQueue.shift())
		})
	}

	async waitForQueue() {
		await waitUntilTrue(
			this.currentRequest.pipe(
				filter(x => !x),
				map(x => !x)
			)
		)
	}

	async addToQueue(action: () => Promise<T>) {
		const id = getRandomId()
		const request = { id, makeRequest: action }
		if (this.currentRequest.value) {
			this.requestQueue.push(request)
		} else {
			this.currentRequest.next(request)
		}

		return new Promise<T>((resolve, reject) => {
			let subscription: Subscription | undefined = undefined
			subscription = this.requestComplete.subscribe({
				next: x => {
					if (x?.id !== id) {
						return
					}
					if (x.successful) {
						resolve(x.result)
					} else {
						reject(x.error)
					}
					subscription?.unsubscribe()
				},
				error: reject
			})
		})
	}
}

export const revalidationCache: Record<string, string> = {}

export const requestQueue: OrderedSequentialQueue<any> = new OrderedSequentialQueue()

export type Optimistic<T> =
	| ({ isOptimistic?: false } & T)
	| ({
			isOptimistic: true
	  } & Partial<T>)

export type ApplyOptimistic<T> = T extends (infer U)[] ? Optimistic<U>[] : Optimistic<T>
export type OptimisticChildren<T, TKeys extends keyof T = never> = {
	[key in keyof T]: key extends TKeys ? ApplyOptimistic<T[key]> : T[key]
}

export class Mutation<
	TMutation extends Endpoint = Endpoint,
	TMutatee extends Endpoint = Endpoint,
	TOptimistic = EndpointResponse<TMutatee>
> {
	constructor(
		public keyTransformer: (
			variables: EndpointInput<TMutation>
		) => { wildcard: true; key: any } | EndpointInput<TMutatee>,
		private transformer?: (
			variables: EndpointInput<TMutation>,
			input: TOptimistic | undefined
		) => TOptimistic | undefined,
		public responseHandler?: (
			response: EndpointResponse<TMutation>,
			variables: EndpointInput<TMutation>
		) => EndpointResponse<TMutatee> | void
	) {}

	transform(variables: EndpointInput<TMutation>, input: TOptimistic | undefined) {
		if (!this.transformer) {
			return input
		}
		return this.transformer(variables, input)
	}
}

type DefaultContextType = {
	previousValues: Record<string, any>
	requestId: string
	mutations: Mutation<any, any, any>[]
}

const isDefaultContext = (x: any): x is DefaultContextType => !!x && x.previousValues && x.requestId && x.mutations
const isSuccessResponse = (x: any): x is { isSuccess: true; response: any } => !!x && x.isSuccess

export type MutationConfig<T extends Endpoint> = UseMutationOptions<
	EndpointNoThrowResult<T>,
	Error,
	EndpointInput<T>,
	DefaultContextType
>

const transformedKeyToHashedKey = (transformedKey: any) => {
	if (isWildcardKey(transformedKey)) {
		return {
			wildcard: true,
			key: transformedKey.key as unknown as any[],
			hashedKey: hashKey([transformedKey.key])
		}
	}
	const key = transformedKey as unknown as any[]
	return {
		wildcard: false,
		key,
		hashedKey: hashKey(key)
	}
}

const buildEnhancedMutationOptions = <TMutation extends Endpoint, TData = EndpointNoThrowResult<TMutation>>(
	queryClient: QueryClient,
	mutationFn: (variables: EndpointInput<TMutation>) => Promise<TData>,
	config: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType> | undefined,
	mutations: Mutation<TMutation, any, any>[]
): UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType> => {
	return {
		...config,
		mutationFn: variables => requestQueue.addToQueue(() => mutationFn(variables)),
		onMutate: async variables => {
			const requestId = getRandomId()
			const previousValues: Record<string, any> = {}

			for (const mutation of mutations) {
				const { wildcard, key, hashedKey } = transformedKeyToHashedKey(mutation.keyTransformer(variables))

				revalidationCache[hashedKey] = requestId
				previousValues[hashedKey] = queryClient.getQueryData(key)
				await queryClient.cancelQueries({ queryKey: key })

				if (!wildcard) {
					queryClient.setQueryData(key, (old: any) =>
						mutation.transform(variables, old !== undefined ? structuredClone(old) : undefined)
					)
				}
			}
			if (config?.onMutate) {
				config.onMutate(variables)
			}
			return {
				previousValues,
				requestId,
				mutations: mutations as unknown as Mutation<any, any, any>[]
			}
		},
		onError: (err, variables, context) => {
			if (!isDefaultContext(context)) {
				return
			}
			if (config?.onError) {
				config.onError(err, variables, context)
			}
			const { requestId, previousValues, mutations } = context
			for (const mutation of mutations) {
				const { wildcard, key, hashedKey } = transformedKeyToHashedKey(
					mutation.keyTransformer(variables as any)
				)

				if (revalidationCache[hashedKey] !== requestId) {
					return
				}
				if (!wildcard && previousValues[hashedKey]) {
					queryClient.setQueryData(key, previousValues[hashedKey])
				}
				queryClient.invalidateQueries({ queryKey: key })
			}
		},
		onSuccess: (data, variables, context) => {
			if (!isDefaultContext(context)) {
				return
			}
			if (config?.onSuccess) {
				config.onSuccess(data, variables, context)
			}
			const { requestId, previousValues, mutations } = context
			for (const mutation of mutations) {
				const { wildcard, key, hashedKey } = transformedKeyToHashedKey(
					mutation.keyTransformer(variables as any)
				)

				if (revalidationCache[hashedKey] !== requestId) {
					return
				}
				if (!isSuccessResponse(data)) {
					if (previousValues[hashedKey]) {
						queryClient.setQueryData(key, previousValues[hashedKey])
					}
					queryClient.invalidateQueries({ queryKey: key })
					return
				}

				if (wildcard || !mutation.responseHandler) {
					queryClient.invalidateQueries({ queryKey: [key] })
					return
				}

				const newData = mutation.responseHandler(data.response, variables as any)
				if (newData) {
					queryClient.setQueryData(key, newData)
				} else {
					queryClient.invalidateQueries({ queryKey: key })
				}
			}
		}
	}
}

export const useEnhancedMutation = <TMutation extends Endpoint, TData = EndpointNoThrowResult<TMutation>>(
	mutationFn: (variables: EndpointInput<TMutation>) => Promise<TData>,
	config?: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType>,
	...mutations: Mutation<TMutation, any, any>[]
) => {
	const queryClient = useQueryClient()
	return useMutation<TData, Error, EndpointInput<TMutation>, DefaultContextType>(
		buildEnhancedMutationOptions(queryClient, mutationFn, config, mutations)
	)
}

/**
 * Like `useEnhancedMutation`, but returns ONLY a referentially-stable trigger function — it never
 * subscribes to mutation state, so the calling component never re-renders on
 * pending/success/error transitions. Built on a `MutationObserver` (the same primitive
 * `useMutation` uses internally) so optimistic updates, the request queue and cache revalidation
 * all behave identically. Use this for fire-and-trigger callbacks passed deep into memoized trees
 * (e.g. a product grid's `onPress`) where the trigger must stay stable across renders.
 */
export const useEnhancedMutationTrigger = <TMutation extends Endpoint, TData = EndpointNoThrowResult<TMutation>>(
	mutationFn: (variables: EndpointInput<TMutation>) => Promise<TData>,
	config?: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType>,
	...mutations: Mutation<TMutation, any, any>[]
) => {
	const queryClient = useQueryClient()
	const observer = useMemo(
		() =>
			new MutationObserver<TData, Error, EndpointInput<TMutation>, DefaultContextType>(
				queryClient,
				buildEnhancedMutationOptions(queryClient, mutationFn, config, mutations)
			),
		// Created once per query client; latest closures are pushed via setOptions below.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryClient]
	)
	// Keep the observer's options (mutationFn/config/mutations closures) fresh without subscribing.
	observer.setOptions(buildEnhancedMutationOptions(queryClient, mutationFn, config, mutations))
	return useCallback((variables: EndpointInput<TMutation>) => observer.mutate(variables), [observer])
}

export type EnhancedMutationTriggerFn<TMutation extends Endpoint, TData = EndpointNoThrowResult<TMutation>> = (
	variables: EndpointInput<TMutation>
) => Promise<TData>

/**
 * Return type of {@link useEnhancedMutationMaybe} / the generated base hooks: the full
 * `UseMutationResult` when subscribing, or just a stable trigger function when not. Narrows on the
 * `Subscribe` type parameter so a literal `true`/`false` argument gives a precise return type.
 */
export type EnhancedMutationApi<
	TMutation extends Endpoint,
	Subscribe extends boolean,
	TData = EndpointNoThrowResult<TMutation>
> = Subscribe extends true
	? UseMutationResult<TData, Error, EndpointInput<TMutation>, DefaultContextType>
	: EnhancedMutationTriggerFn<TMutation, TData>

/**
 * Guards the rules of hooks for {@link useEnhancedMutationMaybe}: the `subscribe` flag selects which
 * hook runs, so it MUST stay constant for the component's lifetime. We throw a clear error instead
 * of letting React blow up with a cryptic hook-order mismatch.
 */
const useStableSubscribe = (subscribe: boolean) => {
	const initial = useRef(subscribe)
	if (initial.current !== subscribe) {
		throw new Error(
			`A mutation hook's \`subscribe\` argument changed from \`${initial.current}\` to \`${subscribe}\`. ` +
				`It selects whether the hook subscribes to mutation state, so it changes which hooks run and must ` +
				`stay constant for the lifetime of the component (rules of hooks).`
		)
	}
}

/**
 * One hook to rule both: pass `subscribe: true` to get the full `useMutation` result (state +
 * `mutate`/`mutateAsync`), or `subscribe: false` to get only a referentially-stable trigger that
 * never re-renders the caller. Lets a single wrapper serve both subscribing and fire-and-forget
 * call sites instead of duplicating each mutation hook.
 */
export function useEnhancedMutationMaybe<
	TMutation extends Endpoint,
	Subscribe extends boolean = true,
	TData = EndpointNoThrowResult<TMutation>
>(
	subscribe: Subscribe,
	mutationFn: (variables: EndpointInput<TMutation>) => Promise<TData>,
	config?: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType>,
	...mutations: Mutation<TMutation, any, any>[]
): EnhancedMutationApi<TMutation, Subscribe, TData> {
	useStableSubscribe(subscribe)
	// `subscribe` is guaranteed stable by `useStableSubscribe`, so the same branch runs every render
	// and hook order stays consistent.
	/* eslint-disable react-hooks/rules-of-hooks */
	const api = subscribe
		? useEnhancedMutation<TMutation, TData>(mutationFn, config, ...mutations)
		: useEnhancedMutationTrigger<TMutation, TData>(mutationFn, config, ...mutations)
	/* eslint-enable react-hooks/rules-of-hooks */
	return api as EnhancedMutationApi<TMutation, Subscribe, TData>
}

const isWildcardKey = (key: any): key is { wildcard: true; key: any } => 'wildcard' in key && key.wildcard

export const createMutation = <
	TMutation extends Endpoint,
	TMutatee extends Endpoint = Endpoint,
	TOptimistic = EndpointResponse<TMutatee>
>(
	endpoint: TMutatee,
	keyTransformer: (input: EndpointInput<TMutation>) => { wildcard: true; key: any } | EndpointInput<TMutatee>,
	transformer?: (variables: EndpointInput<TMutation>, input: TOptimistic | undefined) => TOptimistic | undefined,
	responseHandler?: (
		response: EndpointResponse<TMutation>,
		variables: EndpointInput<TMutation>
	) => EndpointResponse<TMutatee> | void
) => {
	return new Mutation<TMutation, TMutatee, TOptimistic>(
		(input: EndpointInput<TMutation>) => {
			const transform = keyTransformer(input)
			if (isWildcardKey(transform)) {
				return transform.key
			}
			return (QUERY_KEYS as any)[endpoint](transform as EndpointInput<TMutatee>)
		},
		transformer,
		responseHandler
	)
}

export type PreloadContext = {
	api: Api
	queryClient: QueryClient
}

export interface ApiContextValue {
	api: Api
}

const ApiContext = createContext<ApiContextValue>(undefined as any)

export const useApiContext = () => {
	const value = useContext(ApiContext)
	if (!value) {
		throw new Error('useApiContext must be used within a ApiProvider')
	}
	return value
}

export const ApiProvider = ({ api, children }: { api: Api; children: ReactNode }) => {
	const value = useMemo<ApiContextValue>(() => ({ api }), [api])
	return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
}

export const getGetVersionInfoOptions = (api: Api, {}: EndpointInput<Endpoint.GetVersionInfo>) => ({
	queryKey: [Endpoint.GetVersionInfo],
	queryFn: () => api.getVersionInfo({})
})

export const preloadGetVersionInfo = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetVersionInfo>
) => {
	return queryClient.ensureQueryData(getGetVersionInfoOptions(api, input))
}

export const useGetVersionInfo = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetVersionInfo>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetVersionInfoOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetFeaturesOptions = (api: Api, {}: EndpointInput<Endpoint.GetFeatures>) => ({
	queryKey: [Endpoint.GetFeatures],
	queryFn: () => api.getFeatures({})
})

export const preloadGetFeatures = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetFeatures>
) => {
	return queryClient.ensureQueryData(getGetFeaturesOptions(api, input))
}

export const useGetFeatures = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetFeatures>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetFeaturesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCustomerLogin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CustomerLogin>,
	...mutations: Mutation<Endpoint.CustomerLogin, any, any>[]
): EnhancedMutationApi<Endpoint.CustomerLogin, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CustomerLogin, Subscribe>(
		subscribe,
		input => api.customerLoginNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseVerifyBusinessCardPin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.VerifyBusinessCardPin>,
	...mutations: Mutation<Endpoint.VerifyBusinessCardPin, any, any>[]
): EnhancedMutationApi<Endpoint.VerifyBusinessCardPin, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.VerifyBusinessCardPin, Subscribe>(
		subscribe,
		input => api.verifyBusinessCardPinNoThrow(input),
		config,
		...mutations
	)
}

export const getGetCustomerOptions = (api: Api, {}: EndpointInput<Endpoint.GetCustomer>) => ({
	queryKey: [Endpoint.GetCustomer],
	queryFn: () => api.getCustomer({})
})

export const preloadGetCustomer = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCustomer>
) => {
	return queryClient.ensureQueryData(getGetCustomerOptions(api, input))
}

export const useGetCustomer = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCustomer>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCustomerOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseUpdateCustomer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateCustomer>,
	...mutations: Mutation<Endpoint.UpdateCustomer, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateCustomer, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateCustomer, Subscribe>(
		subscribe,
		input => api.updateCustomerNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseAcceptTerms = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AcceptTerms>,
	...mutations: Mutation<Endpoint.AcceptTerms, any, any>[]
): EnhancedMutationApi<Endpoint.AcceptTerms, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.AcceptTerms, Subscribe>(
		subscribe,
		input => api.acceptTermsNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteAccount = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteAccount>,
	...mutations: Mutation<Endpoint.DeleteAccount, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteAccount, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteAccount, Subscribe>(
		subscribe,
		input => api.deleteAccountNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCancelDeletion = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelDeletion>,
	...mutations: Mutation<Endpoint.CancelDeletion, any, any>[]
): EnhancedMutationApi<Endpoint.CancelDeletion, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CancelDeletion, Subscribe>(
		subscribe,
		input => api.cancelDeletionNoThrow(input),
		config,
		...mutations
	)
}

export const getGetCustomerOrdersOptions = (api: Api, {}: EndpointInput<Endpoint.GetCustomerOrders>) => ({
	queryKey: [Endpoint.GetCustomerOrders],
	queryFn: () => api.getCustomerOrders({})
})

export const preloadGetCustomerOrders = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCustomerOrders>
) => {
	return queryClient.ensureQueryData(getGetCustomerOrdersOptions(api, input))
}

export const useGetCustomerOrders = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCustomerOrders>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCustomerOrdersOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetCustomerOrdersGroupedByDateOptions = (
	api: Api,
	{}: EndpointInput<Endpoint.GetCustomerOrdersGroupedByDate>
) => ({
	queryKey: [Endpoint.GetCustomerOrdersGroupedByDate],
	queryFn: () => api.getCustomerOrdersGroupedByDate({})
})

export const preloadGetCustomerOrdersGroupedByDate = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCustomerOrdersGroupedByDate>
) => {
	return queryClient.ensureQueryData(getGetCustomerOrdersGroupedByDateOptions(api, input))
}

export const useGetCustomerOrdersGroupedByDate = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCustomerOrdersGroupedByDate>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCustomerOrdersGroupedByDateOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseTaktikalAuthStart = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.TaktikalAuthStart>,
	...mutations: Mutation<Endpoint.TaktikalAuthStart, any, any>[]
): EnhancedMutationApi<Endpoint.TaktikalAuthStart, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.TaktikalAuthStart, Subscribe>(
		subscribe,
		input => api.taktikalAuthStartNoThrow(input),
		config,
		...mutations
	)
}

export const getTaktikalAuthPollOptions = (api: Api, { path }: EndpointInput<Endpoint.TaktikalAuthPoll>) => ({
	queryKey: [Endpoint.TaktikalAuthPoll, createStableQueryKey(path)],
	queryFn: () => api.taktikalAuthPoll({ path })
})

export const preloadTaktikalAuthPoll = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.TaktikalAuthPoll>
) => {
	return queryClient.ensureQueryData(getTaktikalAuthPollOptions(api, input))
}

export const useTaktikalAuthPoll = (
	path: EndpointPath<Endpoint.TaktikalAuthPoll>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.TaktikalAuthPoll>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getTaktikalAuthPollOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.authRequestUuid && (config?.enabled ?? true)
	})
}

export const getGetLoyaltyCardOptions = (api: Api, {}: EndpointInput<Endpoint.GetLoyaltyCard>) => ({
	queryKey: [Endpoint.GetLoyaltyCard],
	queryFn: () => api.getLoyaltyCard({})
})

export const preloadGetLoyaltyCard = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetLoyaltyCard>
) => {
	return queryClient.ensureQueryData(getGetLoyaltyCardOptions(api, input))
}

export const useGetLoyaltyCard = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetLoyaltyCard>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetLoyaltyCardOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetLoyaltyCardWalletLinkOptions = (
	api: Api,
	{ query }: EndpointInput<Endpoint.GetLoyaltyCardWalletLink>
) => ({
	queryKey: [Endpoint.GetLoyaltyCardWalletLink, createStableQueryKey(query)],
	queryFn: () => api.getLoyaltyCardWalletLink({ query })
})

export const preloadGetLoyaltyCardWalletLink = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetLoyaltyCardWalletLink>
) => {
	return queryClient.ensureQueryData(getGetLoyaltyCardWalletLinkOptions(api, input))
}

export const useGetLoyaltyCardWalletLink = (
	query: EndpointQuery<Endpoint.GetLoyaltyCardWalletLink>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetLoyaltyCardWalletLink>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetLoyaltyCardWalletLinkOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.platform && (config?.enabled ?? true)
	})
}

export const useBaseJoinLoyaltyProgram = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.JoinLoyaltyProgram>,
	...mutations: Mutation<Endpoint.JoinLoyaltyProgram, any, any>[]
): EnhancedMutationApi<Endpoint.JoinLoyaltyProgram, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.JoinLoyaltyProgram, Subscribe>(
		subscribe,
		input => api.joinLoyaltyProgramNoThrow(input),
		config,
		...mutations
	)
}

export const getGetLoyaltyCardHistoryOptions = (
	api: Api,
	{ query }: EndpointInput<Endpoint.GetLoyaltyCardHistory>
) => ({
	queryKey: [Endpoint.GetLoyaltyCardHistory, createStableQueryKey(query)],
	queryFn: () => api.getLoyaltyCardHistory({ query })
})

export const preloadGetLoyaltyCardHistory = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetLoyaltyCardHistory>
) => {
	return queryClient.ensureQueryData(getGetLoyaltyCardHistoryOptions(api, input))
}

export const useGetLoyaltyCardHistory = (
	query: EndpointQuery<Endpoint.GetLoyaltyCardHistory>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetLoyaltyCardHistory>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetLoyaltyCardHistoryOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedGetLoyaltyCardHistory = (query: EndpointQuery<Endpoint.GetLoyaltyCardHistory>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.GetLoyaltyCardHistory, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.getLoyaltyCardHistory({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getStoreGetAppUpdateBranchesOptions = (
	api: Api,
	{}: EndpointInput<Endpoint.StoreGetAppUpdateBranches>
) => ({
	queryKey: [Endpoint.StoreGetAppUpdateBranches],
	queryFn: () => api.storeGetAppUpdateBranches({})
})

export const preloadStoreGetAppUpdateBranches = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.StoreGetAppUpdateBranches>
) => {
	return queryClient.ensureQueryData(getStoreGetAppUpdateBranchesOptions(api, input))
}

export const useStoreGetAppUpdateBranches = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.StoreGetAppUpdateBranches>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getStoreGetAppUpdateBranchesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getStoreGetAppUpdatesOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.StoreGetAppUpdates>
) => ({
	queryKey: [Endpoint.StoreGetAppUpdates, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.storeGetAppUpdates({ path, query })
})

export const preloadStoreGetAppUpdates = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.StoreGetAppUpdates>
) => {
	return queryClient.ensureQueryData(getStoreGetAppUpdatesOptions(api, input))
}

export const useStoreGetAppUpdates = (
	path: EndpointPath<Endpoint.StoreGetAppUpdates>,
	query: EndpointQuery<Endpoint.StoreGetAppUpdates>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.StoreGetAppUpdates>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getStoreGetAppUpdatesOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.branchName && (config?.enabled ?? true)
	})
}

export const usePaginatedStoreGetAppUpdates = (
	path: EndpointPath<Endpoint.StoreGetAppUpdates>,
	query: EndpointQuery<Endpoint.StoreGetAppUpdates>
) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.StoreGetAppUpdates, createStableQueryKey(path), createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.storeGetAppUpdates({ path, query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getGetPointOfSaleInfoOptions = (api: Api, {}: EndpointInput<Endpoint.GetPointOfSaleInfo>) => ({
	queryKey: [Endpoint.GetPointOfSaleInfo],
	queryFn: () => api.getPointOfSaleInfo({})
})

export const preloadGetPointOfSaleInfo = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPointOfSaleInfo>
) => {
	return queryClient.ensureQueryData(getGetPointOfSaleInfoOptions(api, input))
}

export const useGetPointOfSaleInfo = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPointOfSaleInfo>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPointOfSaleInfoOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateAuditLog = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateAuditLog>,
	...mutations: Mutation<Endpoint.CreateAuditLog, any, any>[]
): EnhancedMutationApi<Endpoint.CreateAuditLog, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateAuditLog, Subscribe>(
		subscribe,
		input => api.createAuditLogNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPaymentProvidersOptions = (api: Api, {}: EndpointInput<Endpoint.GetPaymentProviders>) => ({
	queryKey: [Endpoint.GetPaymentProviders],
	queryFn: () => api.getPaymentProviders({})
})

export const preloadGetPaymentProviders = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPaymentProviders>
) => {
	return queryClient.ensureQueryData(getGetPaymentProvidersOptions(api, input))
}

export const useGetPaymentProviders = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPaymentProviders>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPaymentProvidersOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetPromotionsOptions = (api: Api, {}: EndpointInput<Endpoint.GetPromotions>) => ({
	queryKey: [Endpoint.GetPromotions],
	queryFn: () => api.getPromotions({})
})

export const preloadGetPromotions = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPromotions>
) => {
	return queryClient.ensureQueryData(getGetPromotionsOptions(api, input))
}

export const useGetPromotions = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPromotions>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPromotionsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getListSalesChannelsOptions = (api: Api, {}: EndpointInput<Endpoint.ListSalesChannels>) => ({
	queryKey: [Endpoint.ListSalesChannels],
	queryFn: () => api.listSalesChannels({})
})

export const preloadListSalesChannels = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListSalesChannels>
) => {
	return queryClient.ensureQueryData(getListSalesChannelsOptions(api, input))
}

export const useListSalesChannels = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListSalesChannels>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListSalesChannelsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateOrder>,
	...mutations: Mutation<Endpoint.CreateOrder, any, any>[]
): EnhancedMutationApi<Endpoint.CreateOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateOrder, Subscribe>(
		subscribe,
		input => api.createOrderNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseGetOrdersByIds = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.GetOrdersByIds>,
	...mutations: Mutation<Endpoint.GetOrdersByIds, any, any>[]
): EnhancedMutationApi<Endpoint.GetOrdersByIds, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.GetOrdersByIds, Subscribe>(
		subscribe,
		input => api.getOrdersByIdsNoThrow(input),
		config,
		...mutations
	)
}

export const getGetOrderOptions = (api: Api, { path }: EndpointInput<Endpoint.GetOrder>) => ({
	queryKey: [Endpoint.GetOrder, createStableQueryKey(path)],
	queryFn: () => api.getOrder({ path })
})

export const preloadGetOrder = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetOrder>) => {
	return queryClient.ensureQueryData(getGetOrderOptions(api, input))
}

export const useGetOrder = (
	path: EndpointPath<Endpoint.GetOrder>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrder>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const useBaseClearOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ClearOrder>,
	...mutations: Mutation<Endpoint.ClearOrder, any, any>[]
): EnhancedMutationApi<Endpoint.ClearOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ClearOrder, Subscribe>(
		subscribe,
		input => api.clearOrderNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseAddLineItem = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AddLineItem>,
	...mutations: Mutation<Endpoint.AddLineItem, any, any>[]
): EnhancedMutationApi<Endpoint.AddLineItem, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.AddLineItem, Subscribe>(
		subscribe,
		input => api.addLineItemNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateLineItem = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateLineItem>,
	...mutations: Mutation<Endpoint.UpdateLineItem, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateLineItem, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateLineItem, Subscribe>(
		subscribe,
		input => api.updateLineItemNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteLineItem = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteLineItem>,
	...mutations: Mutation<Endpoint.DeleteLineItem, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteLineItem, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteLineItem, Subscribe>(
		subscribe,
		input => api.deleteLineItemNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateProductQuantity = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductQuantity>,
	...mutations: Mutation<Endpoint.UpdateProductQuantity, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductQuantity, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductQuantity, Subscribe>(
		subscribe,
		input => api.updateProductQuantityNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRemoveGiftCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RemoveGiftCard>,
	...mutations: Mutation<Endpoint.RemoveGiftCard, any, any>[]
): EnhancedMutationApi<Endpoint.RemoveGiftCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RemoveGiftCard, Subscribe>(
		subscribe,
		input => api.removeGiftCardNoThrow(input),
		config,
		...mutations
	)
}

export const getGetBagCheckStatusOptions = (api: Api, { path }: EndpointInput<Endpoint.GetBagCheckStatus>) => ({
	queryKey: [Endpoint.GetBagCheckStatus, createStableQueryKey(path)],
	queryFn: () => api.getBagCheckStatus({ path })
})

export const preloadGetBagCheckStatus = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetBagCheckStatus>
) => {
	return queryClient.ensureQueryData(getGetBagCheckStatusOptions(api, input))
}

export const useGetBagCheckStatus = (
	path: EndpointPath<Endpoint.GetBagCheckStatus>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetBagCheckStatus>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetBagCheckStatusOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const useBaseSubmitPaymentInfo = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SubmitPaymentInfo>,
	...mutations: Mutation<Endpoint.SubmitPaymentInfo, any, any>[]
): EnhancedMutationApi<Endpoint.SubmitPaymentInfo, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SubmitPaymentInfo, Subscribe>(
		subscribe,
		input => api.submitPaymentInfoNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCancelOrderPayment = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelOrderPayment>,
	...mutations: Mutation<Endpoint.CancelOrderPayment, any, any>[]
): EnhancedMutationApi<Endpoint.CancelOrderPayment, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CancelOrderPayment, Subscribe>(
		subscribe,
		input => api.cancelOrderPaymentNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseValidateOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ValidateOrder>,
	...mutations: Mutation<Endpoint.ValidateOrder, any, any>[]
): EnhancedMutationApi<Endpoint.ValidateOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ValidateOrder, Subscribe>(
		subscribe,
		input => api.validateOrderNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseInitiateOrderPayment = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.InitiateOrderPayment>,
	...mutations: Mutation<Endpoint.InitiateOrderPayment, any, any>[]
): EnhancedMutationApi<Endpoint.InitiateOrderPayment, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.InitiateOrderPayment, Subscribe>(
		subscribe,
		input => api.initiateOrderPaymentNoThrow(input),
		config,
		...mutations
	)
}

export const getGetOrderPaymentInfoOptions = (api: Api, { path }: EndpointInput<Endpoint.GetOrderPaymentInfo>) => ({
	queryKey: [Endpoint.GetOrderPaymentInfo, createStableQueryKey(path)],
	queryFn: () => api.getOrderPaymentInfo({ path })
})

export const preloadGetOrderPaymentInfo = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetOrderPaymentInfo>
) => {
	return queryClient.ensureQueryData(getGetOrderPaymentInfoOptions(api, input))
}

export const useGetOrderPaymentInfo = (
	path: EndpointPath<Endpoint.GetOrderPaymentInfo>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrderPaymentInfo>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderPaymentInfoOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const getGetExitCodeOptions = (api: Api, { path }: EndpointInput<Endpoint.GetExitCode>) => ({
	queryKey: [Endpoint.GetExitCode, createStableQueryKey(path)],
	queryFn: () => api.getExitCode({ path })
})

export const preloadGetExitCode = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetExitCode>
) => {
	return queryClient.ensureQueryData(getGetExitCodeOptions(api, input))
}

export const useGetExitCode = (
	path: EndpointPath<Endpoint.GetExitCode>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetExitCode>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetExitCodeOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const getGetOrderReceiptOptions = (api: Api, { path }: EndpointInput<Endpoint.GetOrderReceipt>) => ({
	queryKey: [Endpoint.GetOrderReceipt, createStableQueryKey(path)],
	queryFn: () => api.getOrderReceipt({ path })
})

export const preloadGetOrderReceipt = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetOrderReceipt>
) => {
	return queryClient.ensureQueryData(getGetOrderReceiptOptions(api, input))
}

export const useGetOrderReceipt = (
	path: EndpointPath<Endpoint.GetOrderReceipt>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrderReceipt>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderReceiptOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const getGetOrderReceiptUrlOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.GetOrderReceiptUrl>
) => ({
	queryKey: [Endpoint.GetOrderReceiptUrl, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getOrderReceiptUrl({ path, query })
})

export const preloadGetOrderReceiptUrl = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetOrderReceiptUrl>
) => {
	return queryClient.ensureQueryData(getGetOrderReceiptUrlOptions(api, input))
}

export const useGetOrderReceiptUrl = (
	path: EndpointPath<Endpoint.GetOrderReceiptUrl>,
	query: EndpointQuery<Endpoint.GetOrderReceiptUrl>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrderReceiptUrl>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderReceiptUrlOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.orderUuid && !!query.format && (config?.enabled ?? true)
	})
}

export const getGetOrderReceiptPdfOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.GetOrderReceiptPdf>
) => ({
	queryKey: [Endpoint.GetOrderReceiptPdf, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getOrderReceiptPdf({ path, query })
})

export const preloadGetOrderReceiptPdf = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetOrderReceiptPdf>
) => {
	return queryClient.ensureQueryData(getGetOrderReceiptPdfOptions(api, input))
}

export const useGetOrderReceiptPdf = (
	path: EndpointPath<Endpoint.GetOrderReceiptPdf>,
	query: EndpointQuery<Endpoint.GetOrderReceiptPdf>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrderReceiptPdf>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderReceiptPdfOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.orderUuid && !!query.format && (config?.enabled ?? true)
	})
}

export const getGetProductsOptions = (api: Api, { query }: EndpointInput<Endpoint.GetProducts>) => ({
	queryKey: [Endpoint.GetProducts, createStableQueryKey(query)],
	queryFn: () => api.getProducts({ query })
})

export const preloadGetProducts = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProducts>
) => {
	return queryClient.ensureQueryData(getGetProductsOptions(api, input))
}

export const useGetProducts = (
	query: EndpointQuery<Endpoint.GetProducts>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProducts>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetCategoriesOptions = (api: Api, { query }: EndpointInput<Endpoint.GetCategories>) => ({
	queryKey: [Endpoint.GetCategories, createStableQueryKey(query)],
	queryFn: () => api.getCategories({ query })
})

export const preloadGetCategories = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCategories>
) => {
	return queryClient.ensureQueryData(getGetCategoriesOptions(api, input))
}

export const useGetCategories = (
	query: EndpointQuery<Endpoint.GetCategories>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCategories>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCategoriesOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetProductsFromGroupOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.GetProductsFromGroup>
) => ({
	queryKey: [Endpoint.GetProductsFromGroup, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getProductsFromGroup({ path, query })
})

export const preloadGetProductsFromGroup = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductsFromGroup>
) => {
	return queryClient.ensureQueryData(getGetProductsFromGroupOptions(api, input))
}

export const useGetProductsFromGroup = (
	path: EndpointPath<Endpoint.GetProductsFromGroup>,
	query: EndpointQuery<Endpoint.GetProductsFromGroup>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductsFromGroup>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductsFromGroupOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.groupUuid && (config?.enabled ?? true)
	})
}

export const getGetVariantOptions = (api: Api, { path, query }: EndpointInput<Endpoint.GetVariant>) => ({
	queryKey: [Endpoint.GetVariant, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getVariant({ path, query })
})

export const preloadGetVariant = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetVariant>) => {
	return queryClient.ensureQueryData(getGetVariantOptions(api, input))
}

export const useGetVariant = (
	path: EndpointPath<Endpoint.GetVariant>,
	query: EndpointQuery<Endpoint.GetVariant>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetVariant>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetVariantOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.variantUuid && (config?.enabled ?? true)
	})
}

export const getGetVariantFromBarcodeOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.GetVariantFromBarcode>
) => ({
	queryKey: [Endpoint.GetVariantFromBarcode, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getVariantFromBarcode({ path, query })
})

export const preloadGetVariantFromBarcode = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetVariantFromBarcode>
) => {
	return queryClient.ensureQueryData(getGetVariantFromBarcodeOptions(api, input))
}

export const useGetVariantFromBarcode = (
	path: EndpointPath<Endpoint.GetVariantFromBarcode>,
	query: EndpointQuery<Endpoint.GetVariantFromBarcode>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetVariantFromBarcode>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetVariantFromBarcodeOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.barcode && (config?.enabled ?? true)
	})
}

export const QUERY_KEYS = {
	[Endpoint.GetVersionInfo]: ({}: EndpointInput<Endpoint.GetVersionInfo>) => [Endpoint.GetVersionInfo],
	[Endpoint.GetFeatures]: ({}: EndpointInput<Endpoint.GetFeatures>) => [Endpoint.GetFeatures],
	[Endpoint.GetCustomer]: ({}: EndpointInput<Endpoint.GetCustomer>) => [Endpoint.GetCustomer],
	[Endpoint.GetCustomerOrders]: ({}: EndpointInput<Endpoint.GetCustomerOrders>) => [Endpoint.GetCustomerOrders],
	[Endpoint.GetCustomerOrdersGroupedByDate]: ({}: EndpointInput<Endpoint.GetCustomerOrdersGroupedByDate>) => [
		Endpoint.GetCustomerOrdersGroupedByDate
	],
	[Endpoint.TaktikalAuthPoll]: ({ path }: EndpointInput<Endpoint.TaktikalAuthPoll>) => [
		Endpoint.TaktikalAuthPoll,
		createStableQueryKey(path)
	],
	[Endpoint.GetLoyaltyCard]: ({}: EndpointInput<Endpoint.GetLoyaltyCard>) => [Endpoint.GetLoyaltyCard],
	[Endpoint.GetLoyaltyCardWalletLink]: ({ query }: EndpointInput<Endpoint.GetLoyaltyCardWalletLink>) => [
		Endpoint.GetLoyaltyCardWalletLink,
		createStableQueryKey(query)
	],
	[Endpoint.GetLoyaltyCardHistory]: ({ query }: EndpointInput<Endpoint.GetLoyaltyCardHistory>) => [
		Endpoint.GetLoyaltyCardHistory,
		createStableQueryKey(query)
	],
	[Endpoint.StoreGetAppUpdateBranches]: ({}: EndpointInput<Endpoint.StoreGetAppUpdateBranches>) => [
		Endpoint.StoreGetAppUpdateBranches
	],
	[Endpoint.StoreGetAppUpdates]: ({ path, query }: EndpointInput<Endpoint.StoreGetAppUpdates>) => [
		Endpoint.StoreGetAppUpdates,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetPointOfSaleInfo]: ({}: EndpointInput<Endpoint.GetPointOfSaleInfo>) => [Endpoint.GetPointOfSaleInfo],
	[Endpoint.GetPaymentProviders]: ({}: EndpointInput<Endpoint.GetPaymentProviders>) => [Endpoint.GetPaymentProviders],
	[Endpoint.GetPromotions]: ({}: EndpointInput<Endpoint.GetPromotions>) => [Endpoint.GetPromotions],
	[Endpoint.ListSalesChannels]: ({}: EndpointInput<Endpoint.ListSalesChannels>) => [Endpoint.ListSalesChannels],
	[Endpoint.GetOrder]: ({ path }: EndpointInput<Endpoint.GetOrder>) => [
		Endpoint.GetOrder,
		createStableQueryKey(path)
	],
	[Endpoint.GetBagCheckStatus]: ({ path }: EndpointInput<Endpoint.GetBagCheckStatus>) => [
		Endpoint.GetBagCheckStatus,
		createStableQueryKey(path)
	],
	[Endpoint.GetOrderPaymentInfo]: ({ path }: EndpointInput<Endpoint.GetOrderPaymentInfo>) => [
		Endpoint.GetOrderPaymentInfo,
		createStableQueryKey(path)
	],
	[Endpoint.GetExitCode]: ({ path }: EndpointInput<Endpoint.GetExitCode>) => [
		Endpoint.GetExitCode,
		createStableQueryKey(path)
	],
	[Endpoint.GetOrderReceipt]: ({ path }: EndpointInput<Endpoint.GetOrderReceipt>) => [
		Endpoint.GetOrderReceipt,
		createStableQueryKey(path)
	],
	[Endpoint.GetOrderReceiptUrl]: ({ path, query }: EndpointInput<Endpoint.GetOrderReceiptUrl>) => [
		Endpoint.GetOrderReceiptUrl,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetOrderReceiptPdf]: ({ path, query }: EndpointInput<Endpoint.GetOrderReceiptPdf>) => [
		Endpoint.GetOrderReceiptPdf,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetProducts]: ({ query }: EndpointInput<Endpoint.GetProducts>) => [
		Endpoint.GetProducts,
		createStableQueryKey(query)
	],
	[Endpoint.GetCategories]: ({ query }: EndpointInput<Endpoint.GetCategories>) => [
		Endpoint.GetCategories,
		createStableQueryKey(query)
	],
	[Endpoint.GetProductsFromGroup]: ({ path, query }: EndpointInput<Endpoint.GetProductsFromGroup>) => [
		Endpoint.GetProductsFromGroup,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetVariant]: ({ path, query }: EndpointInput<Endpoint.GetVariant>) => [
		Endpoint.GetVariant,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetVariantFromBarcode]: ({ path, query }: EndpointInput<Endpoint.GetVariantFromBarcode>) => [
		Endpoint.GetVariantFromBarcode,
		createStableQueryKey(path),
		createStableQueryKey(query)
	]
}
